import axios from 'axios';

import { api } from './index';

import type {
  AppointmentActionKind,
  AppointmentDeclineCopy,
} from '@/features/appointments/appointmentStatusCopy';
import type {
  AppointmentBrowserContractMarker,
  LegacyAppointmentOperationContractMarker,
} from '@/features/appointments/browserCapability';
import type {
  AppointmentKind,
  AppointmentOperationStatus,
  AppointmentScope,
  BookAppointmentCommand,
  BrowserCoachCancelAppointmentCommandV2,
  BrowserRescheduleSchedulingContext,
  BrowserSchedulingContext,
  CoachAppointmentRescheduleOperation,
  CoachAppointmentRescheduleRequest,
  CoachAppointmentRescheduleResult,
  CoachAppointmentRescheduleReview,
  CoachAppointmentRescheduleReviewRequest,
  CoachCancelAppointmentOperationStatusV2,
  DecodedAppointmentMutationResult,
  DecodedBrowserAppointment,
  DecodedBrowserAppointmentPage,
  DecodedBrowserAppointmentV2,
  DecodedCoachCancelAppointmentMutationResultV2,
  DecodedCoachCancelAppointmentOperationStatusV2,
  ExactBrowserAppointmentPageV2,
} from '@/features/appointments/types';

import {
  appointmentDeclineCopy,
  appointmentDeclineMessage,
} from '@/features/appointments/appointmentStatusCopy';
import {
  APPOINTMENT_BROWSER_CAPABILITY,
  COACH_CANCEL_APPOINTMENT_CONTRACT,
  EXACT_APPOINTMENT_CONTRACT,
  NATIVE_COACH_RESCHEDULE_CONTRACT,
  PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
  SINGLE_SESSION_APPOINTMENT_CONTRACT,
} from '@/features/appointments/browserCapability';
import {
  appointmentContractV1,
  appointmentContractV2,
  appointmentContractV3,
} from '@/features/appointments/contractCodec';
import {
  decodeTrustedAppointmentErrorEnvelope,
  decodeTrustedPlatformAdminAppointmentErrorEnvelope,
} from '@/features/appointments/participantAppointmentError';
import { sameInstant } from '@/features/appointments/sameInstant';
import { safeReloadLocation, safeSetLocationHref } from '@/utils/authRedirect';

const appointmentHttp = axios.create({
  baseURL: import.meta.env.VITE_BASE_API_URL,
  headers: {
    Accept: 'application/json',
  },
});

export type {
  AppointmentBrowserCapability,
  AppointmentBrowserContractMarker,
} from '@/features/appointments/browserCapability';

export const getAppointmentBrowserCapability = () =>
  APPOINTMENT_BROWSER_CAPABILITY;

export {
  decodeTrustedAppointmentErrorEnvelope,
  decodeTrustedPlatformAdminAppointmentErrorEnvelope,
  shouldReconcileAppointmentWrite,
  shouldReconcilePlatformAdminAppointmentWrite,
} from '@/features/appointments/participantAppointmentError';
export type {
  TrustedAppointmentErrorEnvelope,
  TrustedPlatformAdminAppointmentErrorEnvelope,
} from '@/features/appointments/participantAppointmentError';

const getAppointmentBrowserContract = () => appointmentContractV1;

const appointmentUpgradeMemoryGuard = new Set<string>();

export const isPlatformAdminTerminalHydrationForbidden = (
  error: unknown,
): boolean => {
  if (!error || typeof error !== 'object') return false;

  const { data, status } = error as { data?: unknown; status?: unknown };
  const envelope = decodeTrustedPlatformAdminAppointmentErrorEnvelope(data);
  return (
    status === 403 &&
    envelope?.code === 'admin_appointment_terminal_hydration_forbidden' &&
    envelope.write_outcome === 'not_applicable'
  );
};

export const isTrustedCalendarEventTitleStale = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { data, status } = error as { data?: unknown; status?: unknown };
  const envelope = decodeTrustedAppointmentErrorEnvelope(data);
  return (
    status === 409 &&
    envelope?.code === 'calendar_event_title_stale' &&
    envelope.write_outcome === 'not_applied'
  );
};

/**
 * The member-safe copy for a declared non-write, or undefined when the error is
 * not one.
 *
 * Moments answers 503 with `write_outcome: "not_applied"` when it declined to
 * admit an operation at all, and the backend classifies every such response
 * under the single `appointment_upstream_declined` code while passing the
 * specific reason through in `error`. Without the allowlist entry the envelope
 * decode fails and the shared `shouldReconcileAppointmentWrite` fallback treats
 * a definite non-write as ambiguous, because 503 is in
 * AMBIGUOUS_APPOINTMENT_HTTP_STATUSES.
 *
 * That `error` field is an internal machine token, and every booking surface
 * renders it verbatim. It is translated here, at the one place the transport
 * error is decoded, and the token itself is dropped — see
 * `appointmentQueryError`.
 */
const declinedWriteCopy = (
  status: unknown,
  data: unknown,
): AppointmentDeclineCopy | undefined => {
  const envelope = decodeTrustedAppointmentErrorEnvelope(data);
  if (
    status !== 503 ||
    envelope?.code !== 'appointment_upstream_declined' ||
    envelope.write_outcome !== 'not_applied'
  ) {
    return undefined;
  }
  return appointmentDeclineCopy(envelope.error);
};

const isAppointmentDeclineCopy = (
  value: unknown,
): value is AppointmentDeclineCopy =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as AppointmentDeclineCopy).cause === 'string' &&
  typeof (value as AppointmentDeclineCopy).guidance === 'string';

/**
 * Member-safe copy for a trusted declined write, or undefined when the error is
 * not one.
 *
 * Reads the safe fragments `appointmentQueryError` attached to the transport
 * error, so it never handles a reason token. A raw transport envelope is still
 * accepted for callers that construct one directly, because the sanitizing
 * seam is what guarantees the token is gone, not this function.
 */
export const trustedAppointmentDeclineMessage = (
  kind: AppointmentActionKind,
  error: unknown,
): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const { appointmentDecline, data, status } = error as {
    appointmentDecline?: unknown;
    data?: unknown;
    status?: unknown;
  };
  const copy = isAppointmentDeclineCopy(appointmentDecline)
    ? appointmentDecline
    : declinedWriteCopy(status, data);
  return copy === undefined ? undefined : appointmentDeclineMessage(kind, copy);
};

export interface AppointmentUpgradeState {
  requiredMarker: AppointmentBrowserContractMarker;
  state: 'guard-spent' | 'reload-requested';
}

type AppointmentRequestHeaders =
  { get?: (name: string) => unknown } | Record<string, unknown>;

interface AppointmentTransportError {
  appointmentUpgrade?: AppointmentUpgradeState;
  config?: {
    headers?: AppointmentRequestHeaders;
    method?: string;
  };
  response?: { data?: unknown; status?: number };
}

const appointmentBrowserContractMarkers = new Set<string>([
  COACH_CANCEL_APPOINTMENT_CONTRACT,
  EXACT_APPOINTMENT_CONTRACT,
  NATIVE_COACH_RESCHEDULE_CONTRACT,
  PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
  SINGLE_SESSION_APPOINTMENT_CONTRACT,
]);

const requestAppointmentMarker = (
  headers: AppointmentRequestHeaders | undefined,
): AppointmentBrowserContractMarker | undefined => {
  if (!headers) return undefined;
  const headerRecord = headers as Record<string, unknown>;
  const marker =
    'get' in headers && typeof headers.get === 'function'
      ? headers.get('X-Appointment-Contract')
      : (headerRecord['X-Appointment-Contract'] ??
        headerRecord['x-appointment-contract']);
  return typeof marker === 'string' &&
    appointmentBrowserContractMarkers.has(marker)
    ? (marker as AppointmentBrowserContractMarker)
    : undefined;
};

const claimAppointmentUpgradeReload = (
  requiredMarker: AppointmentBrowserContractMarker,
): AppointmentUpgradeState['state'] => {
  const reloadKey = `appointment-contract-upgrade-reload:${requiredMarker}`;
  try {
    if (window.sessionStorage.getItem(reloadKey) === 'requested') {
      return 'guard-spent';
    }
    window.sessionStorage.setItem(reloadKey, 'requested');
    return 'reload-requested';
  } catch {
    if (appointmentUpgradeMemoryGuard.has(requiredMarker)) {
      return 'guard-spent';
    }
    appointmentUpgradeMemoryGuard.add(requiredMarker);
    return 'reload-requested';
  }
};

appointmentHttp.interceptors.request.use((config) => {
  const capability = getAppointmentBrowserCapability();
  const clientVersion = import.meta.env.VITE_APP_VERSION ?? 'unknown';
  if (!config.headers.has('X-Appointment-Contract')) {
    config.headers.set('X-Appointment-Contract', capability.marker);
  }
  config.headers.set('X-Mento-Client-Version', clientVersion);
  return config;
});

appointmentHttp.interceptors.response.use(undefined, (error: unknown) => {
  const transportError = error as AppointmentTransportError;
  const trustedEnvelope = decodeTrustedAppointmentErrorEnvelope(
    transportError.response?.data,
  );
  const method = transportError.config?.method?.toUpperCase();
  const trustedUpgrade =
    transportError.response?.status === 426 &&
    trustedEnvelope?.code === 'appointment_client_upgrade_required' &&
    ((method === 'GET' && trustedEnvelope.write_outcome === 'not_applicable') ||
      (method === 'POST' && trustedEnvelope.write_outcome === 'not_applied'));
  const requiredMarker = requestAppointmentMarker(
    transportError.config?.headers,
  );
  if (trustedUpgrade && requiredMarker) {
    const state = claimAppointmentUpgradeReload(requiredMarker);
    transportError.appointmentUpgrade = {
      requiredMarker,
      state,
    };
    if (state === 'reload-requested') {
      safeReloadLocation();
    }
  }
  return Promise.reject(error);
});

const MAX_APPOINTMENT_PAGE_REQUESTS = 100;
const compareBrowserAppointmentSchedule = (
  left: DecodedBrowserAppointment | DecodedBrowserAppointmentV2,
  right: DecodedBrowserAppointment | DecodedBrowserAppointmentV2,
) =>
  Date.parse(left.currentSchedule.startAt) -
    Date.parse(right.currentSchedule.startAt) ||
  left.id.localeCompare(right.id);

interface GetAppointmentSchedulingContextParamsBase {
  appointmentId?: string;
  coachId: string;
  intent?: 'book' | 'reschedule';
  kind: AppointmentKind;
  memberId?: string;
  /** MEN-5428: whole-series member reschedule; omitted for one occurrence. */
  rescheduleScope?: 'appointment' | 'this_and_following';
  viewerTimezone: string;
  visibleWeeks: number;
}

export type GetAppointmentSchedulingContextParams =
  | (GetAppointmentSchedulingContextParamsBase & {
      bookingPattern: 'one_off';
      browserContractMarker: typeof SINGLE_SESSION_APPOINTMENT_CONTRACT;
    })
  | (GetAppointmentSchedulingContextParamsBase & {
      bookingPattern?: never;
      browserContractMarker?: never;
    });

export interface GetAppointmentOperationParams {
  browserContractMarker: LegacyAppointmentOperationContractMarker;
  operationId: string;
}

export interface GetCoachAppointmentRescheduleOperationV2Params {
  appointmentId: string;
  operationId: string;
  userId: string;
}

export interface GetCoachAppointmentRescheduleSchedulingContextV2Params {
  appointmentId: string;
  candidateDate: string;
  candidateScope: AppointmentScope;
  kind: AppointmentKind;
  // Cache-key discriminant only; authenticated identity is derived server-side.
  userId: string;
  viewerTimezone: string;
  visibleWeeks: number;
}

export interface GetAppointmentParams {
  appointmentId: string;
  // Cache-key discriminant only; not sent on the wire.
  userId?: string;
}

export interface GetNativeCoachAppointmentV2Params {
  appointmentId: string;
  // Cache-key discriminant only; not sent on the wire.
  userId: string;
}

export type GetNativeCoachAppointmentsV2Params =
  | {
      coachId?: never;
      memberId?: never;
      userId: string;
    }
  | {
      coachId: string;
      memberId: string;
      userId: string;
    };

export interface NativeCoachTerminalProjection {
  appointmentVersions: Record<string, number>;
  operationId: string;
  userId: string;
}

export type NativeCoachAppointmentsV2CachePage =
  ExactBrowserAppointmentPageV2 & {
    terminalProjection: NativeCoachTerminalProjection | null;
  };

export const hasMatchingNativeCoachTerminalProjection = (
  page: NativeCoachAppointmentsV2CachePage | undefined,
  userId: string,
) => {
  const marker = page?.terminalProjection;
  if (!marker || marker.userId !== userId) return false;
  const appointmentVersions = Object.entries(marker.appointmentVersions);
  return (
    appointmentVersions.length > 0 &&
    appointmentVersions.every(([appointmentId, version]) =>
      page.appointments.some(
        (appointment) =>
          appointment.id === appointmentId && appointment.version >= version,
      ),
    )
  );
};

export interface GetAppointmentsParams {
  actingRole?: 'member' | 'coach';
  bookingStatus?: 'pending' | 'confirmed' | 'rejected';
  coachId?: string;
  lifecycleStatus?:
    'scheduled' | 'cancellation_pending' | 'cancelled' | 'reschedule_pending';
  memberId?: string;
  progressive?: boolean;
  rangeEnd?: string;
  rangeStart?: string;
  seriesId?: string;
  userId: string;
}

type BrowserAppointmentCachePage = DecodedBrowserAppointmentPage & {
  paginationError?: string;
  paginationPending?: boolean;
};

const fetchBrowserAppointmentPage = async (
  {
    actingRole,
    bookingStatus,
    coachId,
    lifecycleStatus,
    memberId,
    rangeEnd,
    rangeStart,
    seriesId,
  }: GetAppointmentsParams,
  cursor: string | null,
): Promise<DecodedBrowserAppointmentPage> => {
  const result: { data: unknown } = await appointmentHttp.get('/appointments', {
    ...(actingRole
      ? {
          headers: new axios.AxiosHeaders().set(
            'X-Mento-Acting-Role',
            actingRole,
          ),
        }
      : {}),
    params: {
      ...(bookingStatus ? { booking_status: bookingStatus } : {}),
      ...(coachId ? { coach_id: coachId } : {}),
      ...(cursor ? { cursor } : {}),
      ...(lifecycleStatus ? { lifecycle_status: lifecycleStatus } : {}),
      ...(memberId ? { member_id: memberId } : {}),
      ...(rangeEnd ? { range_end: rangeEnd } : {}),
      ...(rangeStart ? { range_start: rangeStart } : {}),
      ...(seriesId ? { series_id: seriesId } : {}),
    },
  });
  return getAppointmentBrowserContract().browserAppointmentPage.decode(
    result.data,
  );
};

interface BookAppointmentRequestBase {
  appointmentCount?: number | null;
  browserContractMarker?: AppointmentBrowserContractMarker;
  cadenceWeeks?: number | null;
  coachId: string;
  idempotencyKey?: string;
  kind: AppointmentKind;
  memberId?: string;
  startAt: string;
  viewerTimezone: string;
}

export interface BookAppointmentRequestV2 extends BookAppointmentRequestBase {
  contractVersion: 'appointment-book.v2';
  expectedCalendarEventTitle: string;
}

export interface BookAppointmentRequestV3 extends BookAppointmentRequestBase {
  bookingPattern: { type: 'one_off' };
  browserContractMarker: typeof SINGLE_SESSION_APPOINTMENT_CONTRACT;
  contractVersion: 'appointment-book.v3';
  expectedCalendarEventTitle: string;
}

export type BookAppointmentRequest =
  | BookAppointmentRequestBase
  | BookAppointmentRequestV2
  | BookAppointmentRequestV3;

interface RescheduleAppointmentRequestBase {
  appointmentId: string;
  expectedVersion: number;
  idempotencyKey: string;
  // MEN-5652: the member's optional note to the coach. Blank is no note; the key is omitted.
  reason?: string | null;
  scope?: AppointmentScope;
  startAt: string;
  // Cache-key discriminant only; not sent on the wire.
  userId?: string;
  viewerTimezone: string;
}

const rescheduleReasonPayload = (
  reason: string | null | undefined,
): { reason?: string } => {
  const trimmed = reason?.trim();
  return trimmed ? { reason: trimmed } : {};
};

interface CancelAppointmentRequestBase {
  appointmentId: string;
  expectedVersion: number;
  idempotencyKey: string;
  replacementAppointmentId?: string | null;
  scope?: AppointmentScope;
  // Cache-key discriminant only; not sent on the wire.
  userId?: string;
}

interface ExactSeriesFenceRequest {
  expectedSeriesMutationVersion: number | null;
  seriesId: string | null;
}

export type RescheduleAppointmentRequest = RescheduleAppointmentRequestBase &
  ExactSeriesFenceRequest;

export interface MemberCancelAppointmentRequestV1
  extends CancelAppointmentRequestBase, ExactSeriesFenceRequest {
  contractVersion: 'appointment-cancel.v1';
  reason: string | null;
}

export interface CoachCancelAppointmentRequestV2 extends BrowserCoachCancelAppointmentCommandV2 {
  appointmentId: string;
  userId?: string;
}

export type CancelAppointmentRequest =
  MemberCancelAppointmentRequestV1 | CoachCancelAppointmentRequestV2;

const exactSeriesFencePayload = (
  command: ExactSeriesFenceRequest & { scope?: AppointmentScope },
) => {
  if (
    !('expectedSeriesMutationVersion' in command) ||
    !('seriesId' in command)
  ) {
    throw new Error(
      'Exact Appointment command requires an explicit Series fence',
    );
  }
  const validStandalone =
    command.seriesId === null &&
    command.expectedSeriesMutationVersion === null &&
    command.scope !== 'this_and_following';
  const validSeries =
    typeof command.seriesId === 'string' &&
    command.seriesId.length > 0 &&
    Number.isInteger(command.expectedSeriesMutationVersion) &&
    (command.expectedSeriesMutationVersion ?? 0) > 0;
  if (!validStandalone && !validSeries) {
    throw new Error('Exact Appointment Series ID and fence are inconsistent');
  }
  return {
    expected_series_mutation_version: command.expectedSeriesMutationVersion,
    series_id: command.seriesId,
  };
};

const decodeLifecycleMutationResult = (
  appointmentId: string,
  value: unknown,
) => {
  const result =
    getAppointmentBrowserContract().browserMutationResult.decode(value);
  if (
    result.status !== 'rejected' &&
    !result.appointments.some((appointment) => appointment.id === appointmentId)
  ) {
    throw new Error('Appointment mutation response id mismatch');
  }
  return result;
};

const decodeCoachCancelMutationResult = (
  appointmentId: string,
  value: unknown,
) => {
  const result = appointmentContractV2.browserMutationResult.decode(value);
  if (
    !result.appointments.some((appointment) => appointment.id === appointmentId)
  ) {
    throw new Error('Appointment mutation response id mismatch');
  }
  return result;
};

type AppointmentInvalidationTag = 'Appointment' | 'Session';
type NativeCoachRescheduleInvalidationTag =
  'Appointment' | 'Session' | { id: string; type: 'Appointment' };

export const appointmentMutationInvalidationTags = (
  result?:
    | DecodedAppointmentMutationResult
    | DecodedCoachCancelAppointmentMutationResultV2,
): AppointmentInvalidationTag[] => {
  if (!result) return [];
  return result.status === 'confirmed'
    ? ['Appointment', 'Session']
    : ['Appointment'];
};

// Booking callers own their attempt-specific confirmation invalidation. The
// shared operation endpoint refreshes caches only for lifecycle commands.
export const appointmentOperationStatusInvalidationTags = (
  result?: AppointmentOperationStatus | CoachCancelAppointmentOperationStatusV2,
): AppointmentInvalidationTag[] => {
  if (
    !result ||
    (result.command !== 'reschedule' && result.command !== 'cancel')
  ) {
    return [];
  }
  if (result.status === 'confirmed') return ['Appointment', 'Session'];
  return result.status === 'pending' ? [] : ['Appointment'];
};

const nativeCoachRescheduleTerminalInvalidationTags = (
  result:
    | CoachAppointmentRescheduleOperation
    | CoachAppointmentRescheduleResult
    | undefined,
  appointmentId: string,
  userId?: string,
): NativeCoachRescheduleInvalidationTag[] => {
  if (
    !result ||
    (result.status !== 'confirmed' &&
      result.status !== 'rejected' &&
      result.status !== 'reconciliation_required')
  ) {
    return [];
  }
  const tags: NativeCoachRescheduleInvalidationTag[] = [
    { id: userId ? `LIST:${userId}` : 'LIST', type: 'Appointment' },
    { id: appointmentId, type: 'Appointment' },
    { id: 'SCHEDULING_CONTEXT', type: 'Appointment' },
    { id: 'COACH_RESCHEDULE_CONTEXT_V2', type: 'Appointment' },
  ];
  if (result.status !== 'rejected') tags.push('Session');
  return tags;
};

const redirectOnAuthRequired = (error: unknown) => {
  try {
    const { response } = error as {
      response?: {
        data?: {
          error?: string;
          mfa_required?: boolean;
          mfaRequired?: boolean;
        };
        status?: number;
      };
    };
    const data = response?.data;
    const mfaRequired =
      response?.status === 403 &&
      (data?.error === 'mfa_required' ||
        data?.mfa_required === true ||
        data?.mfaRequired === true);
    if (mfaRequired && !window.location.pathname.includes('/mfa')) {
      const currentPath = window.location.pathname + window.location.search;
      safeSetLocationHref(
        `/mfa${currentPath ? `?redirect=${encodeURIComponent(currentPath)}` : ''}`,
      );
    }
    if (
      response?.status === 401 &&
      !window.location.pathname.includes('/signin')
    ) {
      const currentPath = window.location.pathname + window.location.search;
      safeSetLocationHref(
        currentPath && currentPath !== '/'
          ? `/signin?redirect=${encodeURIComponent(currentPath)}`
          : '/signin',
      );
    }
  } catch {
    // Redirect handling must never mask the original query error.
  }
};

/**
 * The one place an appointment transport error becomes the object components
 * see. Every appointment endpoint funnels its failures through here, so this is
 * where the "no upstream machine token in member copy" invariant is enforced.
 *
 * A declared non-write carries its reason as an internal token in the
 * envelope's `error` field, and the booking surfaces render that field
 * directly. The token is translated to member-safe copy HERE and then dropped,
 * so an un-updated caller cannot leak it: there is no render path that still
 * receives it. Surfaces that want the action-specific sentence read the
 * attached `appointmentDecline` fragments through
 * `trustedAppointmentDeclineMessage`.
 *
 * The substituted envelope keeps its exact three keys, because
 * `decodeTrustedAppointmentErrorEnvelope` rejects anything else and
 * `shouldReconcileAppointmentWrite` would then fall back to treating this
 * definite non-write as ambiguous.
 */
const appointmentQueryError = (error: unknown, fallback: string) => {
  redirectOnAuthRequired(error);
  const { appointmentUpgrade, response } = error as AppointmentTransportError;
  const data = response?.data ?? fallback;
  const status = response?.status ?? 'PARSING_ERROR';
  const decline = declinedWriteCopy(status, data);
  return {
    data: decline
      ? {
          ...(data as Record<string, unknown>),
          error: appointmentDeclineMessage('request', decline),
        }
      : data,
    status,
    ...(decline ? { appointmentDecline: decline } : {}),
    ...(appointmentUpgrade ? { upgrade: appointmentUpgrade } : {}),
  };
};

export const appointmentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    bookAppointment: builder.mutation<
      DecodedAppointmentMutationResult,
      BookAppointmentCommand | BookAppointmentRequest
    >({
      invalidatesTags: [{ id: 'LIST', type: 'Appointment' }],
      queryFn: async (command) => {
        try {
          const browserContractMarker =
            'browserContractMarker' in command
              ? command.browserContractMarker
              : undefined;
          let data;
          if ('actor' in command) {
            if (command.contractVersion === 'appointment-book.v3') {
              data = appointmentContractV3.bookCommand.encode(command);
            } else if (command.contractVersion === 'appointment-book.v2') {
              data = appointmentContractV2.bookCommand.encode(command);
            } else {
              data = appointmentContractV1.bookCommand.encode(command);
            }
          } else {
            data =
              'contractVersion' in command &&
              command.contractVersion === 'appointment-book.v3'
                ? {
                    booking_pattern: command.bookingPattern,
                    coach_id: command.coachId,
                    expected_calendar_event_title:
                      command.expectedCalendarEventTitle,
                    idempotency_key: command.idempotencyKey,
                    kind: command.kind,
                    start_at: command.startAt,
                    viewer_timezone: command.viewerTimezone,
                  }
                : {
                    appointment_count: command.appointmentCount,
                    cadence_weeks: command.cadenceWeeks,
                    coach_id: command.coachId,
                    idempotency_key: command.idempotencyKey,
                    kind: command.kind,
                    member_id: command.memberId,
                    start_at: command.startAt,
                    viewer_timezone: command.viewerTimezone,
                    ...('contractVersion' in command
                      ? {
                          expected_calendar_event_title:
                            command.expectedCalendarEventTitle,
                        }
                      : {}),
                  };
          }
          const selectedMarker =
            browserContractMarker ??
            ('contractVersion' in command &&
            command.contractVersion === 'appointment-book.v2'
              ? EXACT_APPOINTMENT_CONTRACT
              : PREVIOUS_EXACT_APPOINTMENT_CONTRACT);
          const result = await appointmentHttp.post('/appointments', data, {
            headers: {
              'X-Appointment-Contract': selectedMarker,
            },
          });

          // A durable HTTP 202 admission returns the same mutation-result body
          // as the foreground path plus an optional `retry_after_seconds`
          // polling hint. The strict browser schema forbids unknown keys, so
          // validate the body without the hint and re-attach the decoded
          // interval when it is a positive integer. The existing
          // appointment-operation contract remains the sole source for polling.
          const { retry_after_seconds: rawRetryAfterSeconds, ...mutationBody } =
            (result.data ?? {}) as { retry_after_seconds?: unknown };
          const decoded =
            getAppointmentBrowserContract().browserMutationResult.decode(
              mutationBody,
            );
          const retryAfterSeconds =
            decoded.status === 'pending' &&
            typeof rawRetryAfterSeconds === 'number' &&
            Number.isInteger(rawRetryAfterSeconds) &&
            rawRetryAfterSeconds > 0
              ? rawRetryAfterSeconds
              : null;
          return {
            data: { ...decoded, retryAfterSeconds },
          };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.mutation-result.browser.v1 response',
            ),
          };
        }
      },
    }),
    cancelAppointment: builder.mutation<
      | DecodedAppointmentMutationResult
      | DecodedCoachCancelAppointmentMutationResultV2,
      CancelAppointmentRequest
    >({
      invalidatesTags: (result) => appointmentMutationInvalidationTags(result),
      onQueryStarted: async (
        { userId },
        { dispatch, getState, queryFulfilled },
      ) => {
        if (!userId) return;
        try {
          const { data } = await queryFulfilled;
          if (data.status !== 'confirmed') return;
          const cachedLists = api.util.selectInvalidatedBy(getState(), [
            { id: 'LIST', type: 'Appointment' },
          ]);
          cachedLists.forEach(({ endpointName, originalArgs }) => {
            if (endpointName === 'getAppointments') {
              const args = originalArgs as GetAppointmentsParams;
              if (args.userId !== userId) return;
              dispatch(
                appointmentApi.util.updateQueryData(
                  'getAppointments',
                  args,
                  (draft) => {
                    data.appointments.forEach((appointment) => {
                      const cached = draft.appointments.find(
                        ({ id }) => id === appointment.id,
                      );
                      if (cached && appointment.version >= cached.version) {
                        Object.assign(cached, appointment);
                      }
                    });
                  },
                ),
              );
              return;
            }
            if (endpointName !== 'getNativeCoachAppointmentsV2') return;
            const args = originalArgs as GetNativeCoachAppointmentsV2Params;
            if (args.userId !== userId) return;
            dispatch(
              appointmentApi.util.updateQueryData(
                'getNativeCoachAppointmentsV2',
                args,
                (draft) => {
                  const cachedAppointments = new Map(
                    draft.appointments.map((appointment) => [
                      appointment.id,
                      appointment,
                    ]),
                  );
                  const appointmentVersions: Record<string, number> = {};
                  data.appointments.forEach((appointment) => {
                    const cached = cachedAppointments.get(appointment.id);
                    if (cached && appointment.version >= cached.version) {
                      Object.assign(cached, appointment, {
                        calendarEventId: cached.calendarEventId,
                        contractVersion: 'appointments.v2' as const,
                      });
                      appointmentVersions[appointment.id] = appointment.version;
                    }
                  });
                  if (Object.keys(appointmentVersions).length > 0) {
                    Object.assign(draft, {
                      terminalProjection: {
                        appointmentVersions,
                        operationId: data.operationId,
                        userId,
                      },
                    });
                  }
                  draft.appointments.sort(compareBrowserAppointmentSchedule);
                },
              ),
            );
          });
        } catch {
          // Failed mutations must leave cached appointment pages unchanged.
        }
      },
      queryFn: async ({ appointmentId, userId: _userId, ...command }) => {
        try {
          const coachV2 =
            command.contractVersion === 'appointment-coach-cancel-browser.v2';
          const data = coachV2
            ? appointmentContractV2.browserCoachCancelCommand.encode(command)
            : {
                ...exactSeriesFencePayload(command),
                expected_version: command.expectedVersion,
                idempotency_key: command.idempotencyKey,
                reason: command.reason,
                replacement_appointment_id: command.replacementAppointmentId,
                scope: command.scope ?? 'appointment',
              };
          const result = await appointmentHttp.post(
            `/appointments/${encodeURIComponent(appointmentId)}/cancel`,
            data,
            {
              headers: {
                'X-Appointment-Contract': coachV2
                  ? COACH_CANCEL_APPOINTMENT_CONTRACT
                  : PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
              },
            },
          );

          return {
            data: coachV2
              ? decodeCoachCancelMutationResult(appointmentId, result.data)
              : decodeLifecycleMutationResult(appointmentId, result.data),
          };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.mutation-result.browser.v1 response',
            ),
          };
        }
      },
    }),
    getAppointment: builder.query<
      DecodedBrowserAppointment,
      GetAppointmentParams
    >({
      providesTags: (_result, _error, { appointmentId }) => [
        { id: appointmentId, type: 'Appointment' },
      ],
      queryFn: async ({ appointmentId }) => {
        try {
          const result = await appointmentHttp.get(
            `/appointments/${encodeURIComponent(appointmentId)}`,
          );
          const appointment =
            getAppointmentBrowserContract().browserAppointment.decode(
              result.data,
            );
          if (appointment.id !== appointmentId) {
            throw new Error('Appointment detail response id mismatch');
          }
          return {
            data: appointment,
          };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.projection.browser.v1 response',
            ),
          };
        }
      },
    }),
    getNativeCoachAppointmentV2: builder.query<
      DecodedBrowserAppointmentV2,
      GetNativeCoachAppointmentV2Params
    >({
      providesTags: (_result, _error, { appointmentId }) => [
        { id: appointmentId, type: 'Appointment' },
      ],
      queryFn: async ({ appointmentId }) => {
        try {
          const result = await appointmentHttp.get(
            `/appointments/${encodeURIComponent(appointmentId)}`,
            {
              headers: {
                'X-Appointment-Contract': NATIVE_COACH_RESCHEDULE_CONTRACT,
              },
              params: { contract_version: 'appointments.v2' },
            },
          );
          const appointment = appointmentContractV2.browserAppointment.decode(
            result.data,
          );
          if (appointment.id !== appointmentId) {
            throw new Error(
              'Native coach Appointment detail identity mismatch',
            );
          }
          return { data: appointment };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.projection.browser.v2 response',
            ),
          };
        }
      },
    }),
    getAppointmentOperation: builder.query<
      AppointmentOperationStatus,
      GetAppointmentOperationParams
    >({
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const invalidationTags =
            appointmentOperationStatusInvalidationTags(data);
          // Invalidate specific downstream tags so this operation query never
          // invalidates itself and loops on a terminal response.
          if (invalidationTags.includes('Appointment')) {
            dispatch(
              api.util.invalidateTags([
                { id: 'LIST', type: 'Appointment' },
                { id: 'SCHEDULING_CONTEXT', type: 'Appointment' },
              ]),
            );
          }
          if (invalidationTags.includes('Session')) {
            dispatch(api.util.invalidateTags(['Session']));
          }
        } catch {
          // Operation polling errors must not invalidate Session-backed views.
        }
      },
      providesTags: (_result, _error, { operationId }) => [
        { id: operationId, type: 'Appointment' },
      ],
      queryFn: async ({ browserContractMarker, operationId }) => {
        try {
          const result = await appointmentHttp.get(
            `/appointment-operations/${encodeURIComponent(operationId)}`,
            {
              headers: {
                'X-Appointment-Contract': browserContractMarker,
              },
            },
          );
          const operation =
            getAppointmentBrowserContract().operationStatus.decode(result.data);
          if (operation.id !== operationId) {
            throw new Error('Appointment operation response id mismatch');
          }

          return {
            data: operation,
          };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.operation-status.v1 response',
            ),
          };
        }
      },
    }),
    getCoachCancelAppointmentOperation: builder.query<
      DecodedCoachCancelAppointmentOperationStatusV2,
      { operationId: string; userId: string }
    >({
      onQueryStarted: async (
        { userId },
        { dispatch, getState, queryFulfilled },
      ) => {
        try {
          const { data } = await queryFulfilled;
          if (data.status === 'confirmed') {
            const cachedLists = api.util.selectInvalidatedBy(getState(), [
              { id: `LIST:${userId}`, type: 'Appointment' },
            ]);
            cachedLists.forEach(({ endpointName, originalArgs }) => {
              if (endpointName !== 'getNativeCoachAppointmentsV2') return;
              const args = originalArgs as GetNativeCoachAppointmentsV2Params;
              if (args.userId !== userId) return;
              dispatch(
                appointmentApi.util.updateQueryData(
                  'getNativeCoachAppointmentsV2',
                  args,
                  (draft) => {
                    const cachedAppointments = new Map(
                      draft.appointments.map((appointment) => [
                        appointment.id,
                        appointment,
                      ]),
                    );
                    const appointmentVersions: Record<string, number> = {};
                    data.appointments.forEach((appointment) => {
                      const cached = cachedAppointments.get(appointment.id);
                      if (cached && appointment.version >= cached.version) {
                        Object.assign(cached, appointment, {
                          calendarEventId: cached.calendarEventId,
                          contractVersion: 'appointments.v2' as const,
                        });
                        appointmentVersions[appointment.id] =
                          appointment.version;
                      }
                    });
                    if (Object.keys(appointmentVersions).length > 0) {
                      Object.assign(draft, {
                        terminalProjection: {
                          appointmentVersions,
                          operationId: data.id,
                          userId,
                        },
                      });
                    }
                    draft.appointments.sort(compareBrowserAppointmentSchedule);
                  },
                ),
              );
            });
          }
          const invalidationTags =
            appointmentOperationStatusInvalidationTags(data);
          if (invalidationTags.includes('Appointment')) {
            dispatch(
              api.util.invalidateTags([
                { id: 'LIST', type: 'Appointment' },
                { id: 'SCHEDULING_CONTEXT', type: 'Appointment' },
              ]),
            );
          }
          if (invalidationTags.includes('Session')) {
            dispatch(api.util.invalidateTags(['Session']));
          }
        } catch {
          // Operation polling errors must not invalidate Session-backed views.
        }
      },
      providesTags: (_result, _error, { operationId }) => [
        { id: operationId, type: 'Appointment' },
      ],
      queryFn: async ({ operationId }) => {
        try {
          const result = await appointmentHttp.get(
            `/appointment-operations/${encodeURIComponent(operationId)}`,
            {
              headers: {
                'X-Appointment-Contract':
                  'browser-appointments.coach-cancel-v2',
              },
            },
          );
          const operation = appointmentContractV2.operationStatus.decode(
            result.data,
          );
          if (operation.id !== operationId) {
            throw new Error('Appointment operation response id mismatch');
          }
          return { data: operation };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.operation-status.v2 response',
            ),
          };
        }
      },
    }),
    getAppointmentSchedulingContext: builder.query<
      BrowserSchedulingContext,
      GetAppointmentSchedulingContextParams
    >({
      providesTags: [{ id: 'SCHEDULING_CONTEXT', type: 'Appointment' }],
      queryFn: async ({
        appointmentId,
        bookingPattern,
        browserContractMarker,
        coachId,
        intent = 'book',
        kind,
        memberId,
        rescheduleScope,
        viewerTimezone,
        visibleWeeks,
      }) => {
        try {
          const result = await appointmentHttp.get(
            '/appointments/scheduling-context',
            {
              headers: {
                'X-Appointment-Contract':
                  browserContractMarker ?? EXACT_APPOINTMENT_CONTRACT,
              },
              params: {
                appointment_id: appointmentId,
                ...(bookingPattern ? { booking_pattern: bookingPattern } : {}),
                coach_id: coachId,
                ...(!bookingPattern
                  ? { contract_version: 'appointment-scheduling-context.v2' }
                  : {}),
                intent,
                kind,
                member_id: memberId,
                ...(rescheduleScope
                  ? { reschedule_scope: rescheduleScope }
                  : {}),
                viewer_timezone: viewerTimezone,
                visible_weeks: visibleWeeks,
              },
            },
          );

          return {
            data: bookingPattern
              ? appointmentContractV3.browserSchedulingContext.decode(
                  result.data,
                )
              : appointmentContractV2.browserSchedulingContext.decode(
                  result.data,
                ),
          };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              bookingPattern
                ? 'Invalid appointment.scheduling-context.browser.v3 response'
                : 'Invalid appointment.scheduling-context.browser.v2 response',
            ),
          };
        }
      },
    }),
    getCoachAppointmentRescheduleSchedulingContextV2: builder.query<
      BrowserRescheduleSchedulingContext,
      GetCoachAppointmentRescheduleSchedulingContextV2Params
    >({
      providesTags: [
        { id: 'COACH_RESCHEDULE_CONTEXT_V2', type: 'Appointment' },
      ],
      queryFn: async (command) => {
        try {
          const result = await appointmentHttp.get(
            '/appointments/scheduling-context',
            {
              headers: {
                'X-Appointment-Contract': NATIVE_COACH_RESCHEDULE_CONTRACT,
              },
              params: {
                appointment_id: command.appointmentId,
                candidate_date: command.candidateDate,
                candidate_scope: command.candidateScope,
                intent: 'reschedule',
                kind: command.kind,
                viewer_timezone: command.viewerTimezone,
                visible_weeks: command.visibleWeeks,
              },
            },
          );
          return {
            data: appointmentContractV2.browserRescheduleSchedulingContext.decode(
              result.data,
            ),
          };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.scheduling-context.reschedule.browser.v2 response',
            ),
          };
        }
      },
    }),
    getAppointments: builder.query<
      BrowserAppointmentCachePage,
      GetAppointmentsParams
    >({
      merge: (currentCache, incomingPage) => {
        const currentAppointments = new Map(
          currentCache.appointments.map((appointment) => [
            appointment.id,
            appointment,
          ]),
        );
        const incomingAppointments = incomingPage.appointments.map(
          (appointment) => {
            const current = currentAppointments.get(appointment.id);
            return current && current.version > appointment.version
              ? current
              : appointment;
          },
        );
        if (incomingPage.paginationPending) {
          currentCache.appointments.forEach((appointment) => {
            if (!incomingAppointments.some(({ id }) => id === appointment.id)) {
              incomingAppointments.push(appointment);
            }
          });
        }
        return {
          ...incomingPage,
          appointments: incomingAppointments.sort(
            compareBrowserAppointmentSchedule,
          ),
          paginationError: undefined,
        };
      },
      onQueryStarted: async (
        params,
        { dispatch, getCacheEntry, queryFulfilled, requestId },
      ) => {
        if (!params.progressive) return;
        try {
          const { data: firstPage } = await queryFulfilled;
          const refreshedAppointments = new Map(
            firstPage.appointments.map((appointment) => [
              appointment.id,
              appointment,
            ]),
          );
          const seenCursors = new Set<string>();
          let cursor = firstPage.nextCursor;
          let pageRequests = 1;

          while (cursor && pageRequests < MAX_APPOINTMENT_PAGE_REQUESTS) {
            if (seenCursors.has(cursor)) {
              throw new Error('Appointment pagination cursor repeated');
            }
            seenCursors.add(cursor);
            pageRequests += 1;
            // eslint-disable-next-line no-await-in-loop -- sequential pagination requires the previous page's cursor
            const page = await fetchBrowserAppointmentPage(params, cursor);
            if (getCacheEntry().requestId !== requestId) return;
            page.appointments.forEach((appointment) => {
              const existing = refreshedAppointments.get(appointment.id);
              if (!existing || existing.version <= appointment.version) {
                refreshedAppointments.set(appointment.id, appointment);
              }
            });
            dispatch(
              appointmentApi.util.updateQueryData(
                'getAppointments',
                params,
                (current) => {
                  const appointments = new Map(
                    current.appointments.map((appointment) => [
                      appointment.id,
                      appointment,
                    ]),
                  );
                  page.appointments.forEach((appointment) => {
                    const existing = appointments.get(appointment.id);
                    if (!existing || existing.version <= appointment.version) {
                      appointments.set(appointment.id, appointment);
                    }
                  });
                  Object.assign(current, {
                    appointments: Array.from(appointments.values()).sort(
                      compareBrowserAppointmentSchedule,
                    ),
                    nextCursor: page.nextCursor,
                  });
                },
              ),
            );
            cursor = page.nextCursor;
          }

          if (cursor) {
            throw new Error('Appointment pagination exceeded request limit');
          }
          dispatch(
            appointmentApi.util.updateQueryData(
              'getAppointments',
              params,
              (current) => {
                const currentAppointments = new Map(
                  current.appointments.map((appointment) => [
                    appointment.id,
                    appointment,
                  ]),
                );
                const appointments = Array.from(
                  refreshedAppointments.values(),
                ).map((appointment) => {
                  const existing = currentAppointments.get(appointment.id);
                  return existing && existing.version > appointment.version
                    ? existing
                    : appointment;
                });
                Object.assign(current, {
                  appointments: appointments.sort(
                    compareBrowserAppointmentSchedule,
                  ),
                  nextCursor: null,
                  paginationError: undefined,
                  paginationPending: false,
                });
              },
            ),
          );
        } catch (error: unknown) {
          if (getCacheEntry().requestId !== requestId) return;
          appointmentQueryError(
            error,
            'Invalid appointment.page.browser.v1 response',
          );
          dispatch(
            appointmentApi.util.updateQueryData(
              'getAppointments',
              params,
              (current) => {
                Object.assign(current, {
                  nextCursor: null,
                  paginationError:
                    error instanceof Error
                      ? error.message
                      : 'Appointment pagination failed',
                  paginationPending: false,
                });
              },
            ),
          );
        }
      },
      providesTags: (_result, _error, { userId }) => [
        { id: 'LIST', type: 'Appointment' },
        { id: `LIST:${userId}`, type: 'Appointment' },
      ],
      queryFn: async (params) => {
        const appointments: DecodedBrowserAppointmentPage['appointments'] = [];
        try {
          const seenCursors = new Set<string>();
          let cursor: string | null = null;
          let pageRequests = 0;

          do {
            pageRequests += 1;
            // eslint-disable-next-line no-await-in-loop -- sequential pagination requires the previous page's cursor
            const page = await fetchBrowserAppointmentPage(params, cursor);
            appointments.push(...page.appointments);

            if (params.progressive) {
              return {
                data: {
                  ...page,
                  paginationPending: Boolean(page.nextCursor),
                },
              };
            }

            if (page.nextCursor && seenCursors.has(page.nextCursor)) {
              throw new Error('Appointment pagination cursor repeated');
            }
            if (page.nextCursor) seenCursors.add(page.nextCursor);
            cursor = page.nextCursor;
          } while (cursor && pageRequests < MAX_APPOINTMENT_PAGE_REQUESTS);

          if (cursor) {
            throw new Error('Appointment pagination exceeded request limit');
          }

          return {
            data: {
              appointments,
              contractVersion: 'appointment-page.v1',
              nextCursor: null,
            },
          };
        } catch (error: unknown) {
          const queryError = appointmentQueryError(
            error,
            'Invalid appointment.page.browser.v1 response',
          );
          return {
            error: queryError,
          };
        }
      },
    }),
    getNativeCoachAppointmentsV2: builder.query<
      NativeCoachAppointmentsV2CachePage,
      GetNativeCoachAppointmentsV2Params
    >({
      merge: (currentCache, incoming) => {
        const currentAppointments = new Map(
          currentCache.appointments.map((appointment) => [
            appointment.id,
            appointment,
          ]),
        );
        const marker = currentCache.terminalProjection;
        const markerStillAhead = Boolean(
          marker &&
          Object.entries(marker.appointmentVersions).some(
            ([appointmentId, version]) =>
              !incoming.appointments.some(
                (appointment) =>
                  appointment.id === appointmentId &&
                  appointment.version >= version,
              ),
          ),
        );
        const appointments = incoming.appointments.map((appointment) => {
          const current = currentAppointments.get(appointment.id);
          return current && current.version > appointment.version
            ? current
            : appointment;
        });
        if (markerStillAhead && marker) {
          Object.keys(marker.appointmentVersions).forEach((appointmentId) => {
            if (appointments.some(({ id }) => id === appointmentId)) return;
            const current = currentAppointments.get(appointmentId);
            if (current) appointments.push(current);
          });
        }
        Object.assign(currentCache, incoming, {
          appointments: appointments.sort(compareBrowserAppointmentSchedule),
          terminalProjection: markerStillAhead ? marker : null,
        });
      },
      providesTags: (_result, _error, { userId }) => [
        { id: 'LIST', type: 'Appointment' },
        { id: `LIST:${userId}`, type: 'Appointment' },
      ],
      queryFn: async (params) => {
        const appointments: ExactBrowserAppointmentPageV2['appointments'] = [];
        try {
          const coachId = 'coachId' in params ? params.coachId : undefined;
          const memberId = 'memberId' in params ? params.memberId : undefined;
          if (
            (coachId === undefined) !== (memberId === undefined) ||
            coachId === '' ||
            memberId === ''
          ) {
            throw new Error(
              'Native coach Appointment list requires a complete coach/member pair',
            );
          }
          const listFilters =
            coachId && memberId
              ? { coach_id: coachId, member_id: memberId }
              : {};
          const seenCursors = new Set<string>();
          let cursor: string | null = null;
          let pageRequests = 0;

          do {
            pageRequests += 1;
            // eslint-disable-next-line no-await-in-loop -- sequential pagination requires the previous page's cursor
            const result: { data: unknown } = await appointmentHttp.get(
              '/appointments',
              {
                headers: {
                  'X-Appointment-Contract': NATIVE_COACH_RESCHEDULE_CONTRACT,
                },
                params: {
                  ...(cursor ? { cursor } : {}),
                  contract_version: 'appointment-page.v2',
                  ...listFilters,
                },
              },
            );
            const page = appointmentContractV2.browserAppointmentPage.decode(
              result.data,
            );
            appointments.push(...page.appointments);

            if (page.nextCursor && seenCursors.has(page.nextCursor)) {
              throw new Error('Native coach Appointment cursor repeated');
            }
            if (page.nextCursor) seenCursors.add(page.nextCursor);
            cursor = page.nextCursor;
          } while (cursor && pageRequests < MAX_APPOINTMENT_PAGE_REQUESTS);

          if (cursor) {
            throw new Error(
              'Native coach Appointment pagination exceeded request limit',
            );
          }
          return {
            data: {
              appointments,
              contractVersion: 'appointment-page.v2',
              nextCursor: null,
              terminalProjection: null,
            },
          };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.page.browser.v2 response',
            ),
          };
        }
      },
    }),
    rescheduleAppointment: builder.mutation<
      DecodedAppointmentMutationResult,
      RescheduleAppointmentRequest
    >({
      invalidatesTags: (result) => appointmentMutationInvalidationTags(result),
      onQueryStarted: async (
        { userId },
        { dispatch, getState, queryFulfilled },
      ) => {
        if (!userId) return;
        try {
          const { data } = await queryFulfilled;
          if (data.status !== 'confirmed') return;
          const cachedLists = api.util.selectInvalidatedBy(getState(), [
            { id: 'LIST', type: 'Appointment' },
          ]);
          cachedLists.forEach(({ endpointName, originalArgs }) => {
            if (endpointName !== 'getAppointments') return;
            const args = originalArgs as GetAppointmentsParams;
            if (args.userId !== userId) return;
            dispatch(
              appointmentApi.util.updateQueryData(
                'getAppointments',
                args,
                (draft) => {
                  data.appointments.forEach((appointment) => {
                    const cached = draft.appointments.find(
                      ({ id }) => id === appointment.id,
                    );
                    if (cached && appointment.version >= cached.version) {
                      Object.assign(cached, appointment);
                    }
                  });
                },
              ),
            );
          });
        } catch {
          // Failed mutations must leave cached appointment pages unchanged.
        }
      },
      queryFn: async ({ appointmentId, ...command }) => {
        try {
          const result = await appointmentHttp.post(
            `/appointments/${encodeURIComponent(appointmentId)}/reschedule`,
            {
              ...exactSeriesFencePayload(command),
              ...rescheduleReasonPayload(command.reason),
              expected_version: command.expectedVersion,
              idempotency_key: command.idempotencyKey,
              scope: command.scope ?? 'appointment',
              start_at: command.startAt,
              viewer_timezone: command.viewerTimezone,
            },
            {
              headers: {
                'X-Appointment-Contract': PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
              },
            },
          );

          return {
            data: decodeLifecycleMutationResult(appointmentId, result.data),
          };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.mutation-result.browser.v1 response',
            ),
          };
        }
      },
    }),
    rescheduleCoachAppointmentV2: builder.mutation<
      CoachAppointmentRescheduleResult,
      CoachAppointmentRescheduleRequest
    >({
      onQueryStarted: async (
        { appointmentId },
        { dispatch, getState, queryFulfilled },
      ) => {
        const cachedLists = api.util.selectInvalidatedBy(getState(), [
          { id: 'LIST', type: 'Appointment' },
        ]);
        try {
          const { data } = await queryFulfilled;
          if (data.status === 'confirmed')
            cachedLists.forEach(({ endpointName, originalArgs }) => {
              if (endpointName !== 'getNativeCoachAppointmentsV2') return;
              const args = originalArgs as GetNativeCoachAppointmentsV2Params;
              dispatch(
                appointmentApi.util.updateQueryData(
                  'getNativeCoachAppointmentsV2',
                  args,
                  (draft) => {
                    const cachedAppointments = new Map(
                      draft.appointments.map((appointment) => [
                        appointment.id,
                        appointment,
                      ]),
                    );
                    const appointmentVersions: Record<string, number> = {};
                    data.appointments.forEach((appointment) => {
                      const cached = cachedAppointments.get(appointment.id);
                      if (cached && appointment.version >= cached.version) {
                        Object.assign(cached, appointment, {
                          calendarEventId: cached.calendarEventId,
                          contractVersion: 'appointments.v2' as const,
                        });
                        appointmentVersions[appointment.id] =
                          appointment.version;
                      }
                    });
                    if (Object.keys(appointmentVersions).length > 0) {
                      Object.assign(draft, {
                        terminalProjection: {
                          appointmentVersions,
                          operationId: data.operationId,
                          userId: args.userId,
                        },
                      });
                    }
                    draft.appointments.sort(compareBrowserAppointmentSchedule);
                  },
                ),
              );
            });
          const tags = nativeCoachRescheduleTerminalInvalidationTags(
            data,
            appointmentId,
          );
          if (tags.length > 0) dispatch(api.util.invalidateTags(tags));
        } catch {
          // Failed or non-terminal mutations must leave cached pages unchanged.
        }
      },
      queryFn: async ({ appointmentId, ...command }) => {
        try {
          const body = appointmentContractV2.browserRescheduleCommand.encode({
            ...command,
            contractVersion: 'appointment-browser-reschedule.v2',
          });
          const result = await appointmentHttp.post(
            `/appointments/${encodeURIComponent(appointmentId)}/reschedule`,
            body,
            {
              headers: {
                'X-Appointment-Contract': NATIVE_COACH_RESCHEDULE_CONTRACT,
              },
            },
          );
          const decoded = appointmentContractV2.browserRescheduleResult.decode(
            result.data,
          );
          if (
            (decoded.status === 'confirmed' ||
              decoded.appointments.length > 0) &&
            !decoded.appointments.some(({ id }) => id === appointmentId)
          ) {
            throw new Error('Coach reschedule result identity mismatch');
          }
          return { data: decoded };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.reschedule-result.browser.v2 response',
            ),
          };
        }
      },
    }),
    reviewCoachAppointmentRescheduleV2: builder.mutation<
      CoachAppointmentRescheduleReview,
      CoachAppointmentRescheduleReviewRequest
    >({
      queryFn: async ({ appointmentId, ...command }) => {
        try {
          const body =
            appointmentContractV2.browserRescheduleReviewCommand.encode({
              ...command,
              contractVersion: 'appointment-browser-reschedule-review.v2',
            });
          const result = await appointmentHttp.post(
            `/appointments/${encodeURIComponent(appointmentId)}/reschedule-review`,
            body,
            {
              headers: {
                'X-Appointment-Contract': NATIVE_COACH_RESCHEDULE_CONTRACT,
              },
            },
          );
          const decoded = appointmentContractV2.browserRescheduleReview.decode(
            result.data,
          );
          if (
            decoded.seriesId !== command.seriesId ||
            decoded.scope !== command.scope ||
            !sameInstant(
              decoded.proposedSchedule.startAt,
              command.proposedStartAt,
            )
          ) {
            throw new Error('Coach reschedule review identity mismatch');
          }
          return { data: decoded };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.reschedule-review.browser.v2 response',
            ),
          };
        }
      },
    }),
    getCoachAppointmentRescheduleOperationV2: builder.query<
      CoachAppointmentRescheduleOperation,
      GetCoachAppointmentRescheduleOperationV2Params
    >({
      onQueryStarted: async (
        { appointmentId, userId },
        { dispatch, getState, queryFulfilled },
      ) => {
        try {
          const { data } = await queryFulfilled;
          if (data.status === 'confirmed') {
            const cachedLists = api.util.selectInvalidatedBy(getState(), [
              { id: `LIST:${userId}`, type: 'Appointment' },
            ]);
            cachedLists.forEach(({ endpointName, originalArgs }) => {
              if (endpointName === 'getAppointments') {
                const args = originalArgs as GetAppointmentsParams;
                if (args.userId !== userId) return;
                dispatch(
                  appointmentApi.util.updateQueryData(
                    'getAppointments',
                    args,
                    (draft) => {
                      const cachedAppointments = new Map(
                        draft.appointments.map((appointment) => [
                          appointment.id,
                          appointment,
                        ]),
                      );
                      data.appointments.forEach((appointment) => {
                        const cached = cachedAppointments.get(appointment.id);
                        if (cached && appointment.version >= cached.version) {
                          Object.assign(cached, appointment);
                        }
                      });
                      draft.appointments.sort(
                        compareBrowserAppointmentSchedule,
                      );
                    },
                  ),
                );
                return;
              }
              if (endpointName !== 'getNativeCoachAppointmentsV2') return;
              const args = originalArgs as GetNativeCoachAppointmentsV2Params;
              if (args.userId !== userId) return;
              dispatch(
                appointmentApi.util.updateQueryData(
                  'getNativeCoachAppointmentsV2',
                  args,
                  (draft) => {
                    const cachedAppointments = new Map(
                      draft.appointments.map((appointment) => [
                        appointment.id,
                        appointment,
                      ]),
                    );
                    const appointmentVersions: Record<string, number> = {};
                    data.appointments.forEach((appointment) => {
                      const cached = cachedAppointments.get(appointment.id);
                      if (cached && appointment.version >= cached.version) {
                        Object.assign(cached, appointment, {
                          calendarEventId: cached.calendarEventId,
                          contractVersion: 'appointments.v2' as const,
                        });
                        appointmentVersions[appointment.id] =
                          appointment.version;
                      }
                    });
                    if (Object.keys(appointmentVersions).length > 0) {
                      Object.assign(draft, {
                        terminalProjection: {
                          appointmentVersions,
                          operationId: data.id,
                          userId,
                        },
                      });
                    }
                    draft.appointments.sort(compareBrowserAppointmentSchedule);
                  },
                ),
              );
            });
          }
          const tags = nativeCoachRescheduleTerminalInvalidationTags(
            data,
            appointmentId,
            userId,
          );
          if (tags.length > 0) dispatch(api.util.invalidateTags(tags));
        } catch {
          // Polling failures and non-terminal responses must not invalidate caches.
        }
      },
      queryFn: async ({ appointmentId, operationId }) => {
        try {
          const result = await appointmentHttp.get(
            `/appointment-operations/${encodeURIComponent(operationId)}`,
            {
              headers: {
                'X-Appointment-Contract': NATIVE_COACH_RESCHEDULE_CONTRACT,
              },
            },
          );
          const decoded =
            appointmentContractV2.browserRescheduleOperation.decode(
              result.data,
            );
          if (
            decoded.id !== operationId ||
            (decoded.appointments.length > 0 &&
              !decoded.appointments.some(({ id }) => id === appointmentId))
          ) {
            throw new Error('Coach reschedule operation identity mismatch');
          }
          return { data: decoded };
        } catch (error: unknown) {
          return {
            error: appointmentQueryError(
              error,
              'Invalid appointment.operation-status.v2 response',
            ),
          };
        }
      },
    }),
  }),
});

export const {
  useBookAppointmentMutation,
  useCancelAppointmentMutation,
  useGetAppointmentQuery,
  useGetNativeCoachAppointmentV2Query,
  useLazyGetNativeCoachAppointmentV2Query,
  useLazyGetAppointmentQuery,
  useLazyGetAppointmentsQuery,
  useGetAppointmentOperationQuery,
  useGetCoachCancelAppointmentOperationQuery,
  useGetCoachAppointmentRescheduleOperationV2Query,
  useGetCoachAppointmentRescheduleSchedulingContextV2Query,
  useGetAppointmentsQuery,
  useGetNativeCoachAppointmentsV2Query,
  useGetAppointmentSchedulingContextQuery,
  useRescheduleAppointmentMutation,
  useRescheduleCoachAppointmentV2Mutation,
  useReviewCoachAppointmentRescheduleV2Mutation,
} = appointmentApi;
